import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  WizardStepComponent,
  ZendraLocalizationServices,
} from '@zendra/shared';

import type { IDescriptionRow } from '../interfaces/IDescriptionRow';
import type { IStepDefineResultingStandsData } from '../interfaces/IStepDefineResultingStandsData';
import type { IStepDescriptionAndCompletionData } from '../interfaces/IStepDescriptionAndCompletionData';
import type { IStepSelectStandToDivideData } from '../interfaces/IStepSelectStandToDivideData';
import type {
  AttributeInheritanceOriginalSummary,
  AttributeInheritanceRow,
  StepDefineAttributeInheritanceData,
} from '../step-define-attribute-inheritance/step-define-attribute-inheritance.models';
@Component({
  selector: 'lib-step-description-and-completion',
  standalone: true,
  imports: [LocalizationPipe, ReactiveFormsModule],
  templateUrl: './step-description-and-completion.html',
})
export class StepDescriptionAndCompletion
  implements WizardStepComponent<IStepDescriptionAndCompletionData>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);

  readonly localizationKeyPrefix = 'Geographic::StandDivision.StepDescriptionAndCompletion';
  readonly previousStepId = 4;

  readonly originalStandId = signal<string | null>(null);
  readonly originalStandName = signal('');
  readonly originalStandArea = signal<number | null>(null);
  readonly originalStandRotation = signal<string>('-----');
  readonly originalStandIsLastCycle = signal(false);
  readonly originalStandIsLastRotation = signal(false);
  readonly originalSummary = signal<AttributeInheritanceOriginalSummary | null>(null);
  readonly resultingRows = signal<AttributeInheritanceRow[]>([]);
  readonly sourceSignature = signal('');

  readonly form = this.formBuilder.group({
    transformationReason: ['', [Validators.required]],
  });

  readonly initialRows = computed<IDescriptionRow[]>(() => {
    const standName = this.originalStandName();
    const standArea = this.originalStandArea();
    const originalSummary = this.originalSummary();
    if (!standName || standArea === null) {
      return [];
    }

    return [
      {
        id: 'initial-row',
        stand: standName,
        cycle: originalSummary?.cycle ?? '-----',
        rotation: this.originalStandRotation(),
        area: standArea,
        isLastCycle: this.originalStandIsLastCycle(),
        isLastRotation: this.originalStandIsLastRotation(),
      },
    ];
  });

  readonly finalRows = computed<IDescriptionRow[]>(() =>
    this.resultingRows().map(row => ({
      id: row.id,
      stand: row.name,
      cycle: this.originalSummary()?.cycle ?? '-----',
      rotation:
        row.id === 'original-stand'
          ? this.originalStandRotation()
          : row.configuration?.rotation?.trim() || '-----',
      area: row.area,
      isLastCycle:
        row.id === 'original-stand' ? this.originalStandIsLastCycle() : true,
      isLastRotation:
        row.id === 'original-stand' ? this.originalStandIsLastRotation() : true,
    })),
  );

  readonly initialTotalArea = computed(() =>
    this.initialRows().reduce((sum, row) => sum + row.area, 0),
  );
  readonly finalTotalArea = computed(() =>
    this.finalRows().reduce((sum, row) => sum + row.area, 0),
  );

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const selectedStandData = context.getStepState<IStepSelectStandToDivideData>(1)?.data;
    const previousStepData =
      context.getStepState<StepDefineAttributeInheritanceData>(this.previousStepId)?.data;
    const resultingStandsData =
      context.getStepState<IStepDefineResultingStandsData>(2)?.data;
    const currentState =
      context.getStepState<IStepDescriptionAndCompletionData>(context.activeStepId())?.data;
    const sourceSignature = this.buildSourceSignature(previousStepData);
    const canRestore = !!currentState && currentState.sourceSignature === sourceSignature;

    if (!canRestore && currentState) {
      context.patchStepState(context.activeStepId(), {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

    this.originalStandId.set(
      previousStepData?.originalStandId ?? resultingStandsData?.originalStandId ?? null,
    );
    this.originalStandName.set(
      previousStepData?.originalStandName ?? resultingStandsData?.originalStandName ?? '',
    );
    this.originalStandArea.set(
      selectedStandData?.standArea ??
        selectedStandData?.standDetail?.standArea ??
        previousStepData?.originalStandArea ??
        resultingStandsData?.originalStandArea ??
        null,
    );
    this.originalStandRotation.set(
      this.formatNumericValue(selectedStandData?.standDetail?.rotationValue),
    );
    this.originalStandIsLastCycle.set(selectedStandData?.standDetail?.isLastCycle === true);
    this.originalStandIsLastRotation.set(selectedStandData?.standDetail?.isLastRotation === true);
    this.originalSummary.set(previousStepData?.originalSummary ?? null);
    this.resultingRows.set(this.buildReferenceRows(previousStepData, resultingStandsData));
    this.sourceSignature.set(sourceSignature);
    this.form.reset(
      {
        transformationReason: canRestore ? currentState.transformationReason : '',
      },
      { emitEvent: false },
    );
  }

  canLeave(): boolean {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivision.StepDescriptionAndCompletion.RequiredFields`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): IStepDescriptionAndCompletionData {
    const data: IStepDescriptionAndCompletionData = {
      sourceSignature: this.sourceSignature(),
      transformationReason: this.form.get('transformationReason')?.value?.trim() ?? '',
      initialRows: this.initialRows(),
      finalRows: this.finalRows(),
      initialTotalArea: this.roundArea(this.initialTotalArea()),
      finalTotalArea: this.roundArea(this.finalTotalArea()),
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: this.form.valid,
      completed: true,
      data,
    });
    return data;
  }

  fieldInvalid(field: 'transformationReason'): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-----';
    }

    return value.toFixed(2).replace('.', ',');
  }

  private buildReferenceRows(
    attributeInheritanceData: StepDefineAttributeInheritanceData | undefined,
    resultingStandsData: IStepDefineResultingStandsData | undefined,
  ): AttributeInheritanceRow[] {
    const configuredRows = attributeInheritanceData?.resultingStands ?? [];
    if (configuredRows.length > 0) {
      const originalRow = resultingStandsData?.rows.find(row => row.type === 'original');
      if (!originalRow || configuredRows.some(row => row.id === originalRow.id)) {
        return configuredRows;
      }

      return [
        {
          id: originalRow.id,
          name: originalRow.name,
          area: originalRow.area,
          configuration: null,
        },
        ...configuredRows,
      ];
    }

    return (resultingStandsData?.rows ?? []).map(row => ({
      id: row.id,
      name: row.name,
      area: row.area,
      configuration: null,
    }));
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private buildSourceSignature(
    attributeInheritanceData: StepDefineAttributeInheritanceData | undefined,
  ): string {
    return JSON.stringify(attributeInheritanceData ?? null);
  }

  private formatNumericValue(value: number | null | undefined): string {
    return value === null || value === undefined ? '-----' : String(value);
  }
}
